import { tagsByIdToList, toCatalogRecord, toTagId, toTagMeta } from './normalizeCatalogTag'

/** @type {Map<string, Record<string, import('./normalizeCatalogTag.js').TagsCatalogRecord>>} */
const orgTagsById = new Map()
/** @type {Map<string, string | null>} */
const orgPinnedTagGroupName = new Map()
const orgListeners = new Map()

const getListenerSet = (orgId) => {
  if (!orgListeners.has(orgId)) {
    orgListeners.set(orgId, new Set())
  }
  return orgListeners.get(orgId)
}

export const getOrgTagsById = (orgId) => {
  if (!orgId) return {}
  if (!orgTagsById.has(orgId)) {
    orgTagsById.set(orgId, {})
  }
  return orgTagsById.get(orgId)
}

export const getTagsListFromMemory = (orgId) => tagsByIdToList(getOrgTagsById(orgId))

const notifyOrgListeners = (orgId) => {
  const listeners = orgListeners.get(orgId)
  if (!listeners?.size) return
  const tagsById = getOrgTagsById(orgId)
  listeners.forEach((listener) => {
    try {
      listener(tagsById, getTagsListFromMemory(orgId))
    } catch (error) {
      console.error('tagsCatalog listener error:', error)
    }
  })
}

/**
 * Replace the org catalog with an id-keyed record map.
 * @param {string} orgId
 * @param {Record<string, import('./normalizeCatalogTag.js').TagsCatalogRecord>} tagsById
 */
export const setOrgTagsByIdInMemory = (orgId, tagsById = {}) => {
  if (!orgId) return
  orgTagsById.set(orgId, { ...tagsById })
  notifyOrgListeners(orgId)
}

const catalogRecordsEqual = (left, right) =>
  Boolean(left && right) &&
  left.tag_name === right.tag_name &&
  left.group_name === right.group_name &&
  left.color_code === right.color_code &&
  Boolean(left.deleted) === Boolean(right.deleted)

export const upsertTagInMemory = (orgId, tag) => {
  if (!orgId || !tag) return
  const tagId = toTagId(tag)
  if (!tagId) return

  const catalog = getOrgTagsById(orgId)
  const existing = catalog[tagId]
  const record = toCatalogRecord(tag, existing)
  if (!record) return

  const unchanged = catalogRecordsEqual(existing, record)
  catalog[tagId] = record
  if (!unchanged) {
    notifyOrgListeners(orgId)
  }
}

export const removeTagFromMemory = (orgId, tagId) => {
  if (!orgId || !tagId) return
  delete getOrgTagsById(orgId)[String(tagId)]
  notifyOrgListeners(orgId)
}

export const getOrgPinnedTagGroupNameFromMemory = (orgId) => {
  if (!orgId) return null
  return orgPinnedTagGroupName.get(orgId) ?? null
}

export const setOrgPinnedTagGroupNameInMemory = (orgId, groupName) => {
  if (!orgId) return
  const nextName = groupName ? String(groupName) : null
  if (orgPinnedTagGroupName.get(orgId) === nextName) return
  orgPinnedTagGroupName.set(orgId, nextName)
  notifyOrgListeners(orgId)
}

export const clearOrgTagsInMemory = (orgId) => {
  if (!orgId) return
  orgTagsById.delete(orgId)
  orgPinnedTagGroupName.delete(orgId)
  orgListeners.delete(orgId)
}

export const getTagByIdFromMemory = (orgId, tagId) => {
  if (!orgId || !tagId) return null
  return toTagMeta(String(tagId), getOrgTagsById(orgId)[String(tagId)])
}

export const subscribeOrgTagsInMemory = (orgId, listener) => {
  if (!orgId || typeof listener !== 'function') {
    return () => {}
  }
  const listeners = getListenerSet(orgId)
  listeners.add(listener)
  listener(getOrgTagsById(orgId), getTagsListFromMemory(orgId))
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      orgListeners.delete(orgId)
    }
  }
}

export const clearAllTagsInMemory = () => {
  orgTagsById.clear()
  orgPinnedTagGroupName.clear()
  orgListeners.clear()
}

export const resetTagsCatalogMemoryForTests = () => {
  clearAllTagsInMemory()
}
